import { THIRD_PARTY_UI_VARIANT, USER_TYPE_CUSTOMER } from "le-coffre-resources/dist/MemberTypes";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { resolveBrowserPublicFrontBaseUrl } from "@Front/Api/Auth/IdNot/helpers/idNotOAuthPublicBaseUrl";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { ThirdPartyStore } from "@Front/Stores/ThirdPartyStore";
import { UserStore } from "@Front/Stores/UserStore";
import { LECOFFRE_SESSION_COOKIE_NAMES, type LeCoffreAuthContext } from "@Front/Utils/lecoffreSessionCookieHelpers";
import {
	isCustomerJwtPayload,
	isUserJwtPayload,
	isThirdPartyJwtPayload,
	isIdNotNotaryPayload,
	type AnyJwtPayload,
} from "@Front/Types/JwtPayloads";
import { revokeIdNotCookiesViaIframe } from "./revokeIdNotCookiesViaIframe";

type ResolveSessionFailureRedirectUrlParams = {
	isThirdParty: boolean;
	isCustomer: boolean;
};

export function clearBrowserSessionArtifacts(): void {
	const expired = `=;expires=${new Date(0).toUTCString()};path=/`;
	for (const name of LECOFFRE_SESSION_COOKIE_NAMES) {
		document.cookie = `${name}${expired}`;
	}
	localStorage.clear();
	LoggerService.info("✅ [SessionRefresh] Auth cookies et localStorage nettoyés (sessionStorage préservé pour was_third_party)");
}

export function resolveSessionFailureRedirectUrl(params: ResolveSessionFailureRedirectUrlParams): string {
	const { isThirdParty, isCustomer } = params;
	const base = resolveBrowserPublicFrontBaseUrl();
	if (isThirdParty) {
		const redirectUrl = `${base}/third-party/login`;
		LoggerService.info("🔄 [SessionRefresh] Redirection vers login tiers", { redirectUrl, userType: THIRD_PARTY_UI_VARIANT });
		return redirectUrl;
	}
	const redirectUrl = base ? `${base}/` : "/";
	LoggerService.info("🔄 [SessionRefresh] Redirection vers la home", {
		redirectUrl,
		userType: isCustomer ? USER_TYPE_CUSTOMER : "notary",
	});
	return redirectUrl;
}

export async function disconnectStoreForAuthContext(context: LeCoffreAuthContext): Promise<void> {
	if (context === "customer") {
		await CustomerStore.instance.disconnect();
		return;
	}
	if (context === "thirdparty") {
		await ThirdPartyStore.instance.disconnect();
		return;
	}
	await UserStore.instance.disconnect();
}

export async function disconnectSessionStoreFromPayload(payload: AnyJwtPayload): Promise<void> {
	if (isCustomerJwtPayload(payload)) {
		await CustomerStore.instance.disconnect();
		LoggerService.info("✅ [SessionRefresh] Session client LeCoffre nettoyée");
		return;
	}
	if (isThirdPartyJwtPayload(payload)) {
		await ThirdPartyStore.instance.disconnect();
		LoggerService.info("✅ [SessionRefresh] Session tiers LeCoffre nettoyée");
		return;
	}
	await UserStore.instance.disconnect();
	LoggerService.info("✅ [SessionRefresh] Session notaire LeCoffre nettoyée");
}

export function redirectAfterSessionCleared(params: ResolveSessionFailureRedirectUrlParams, logLabel: string): void {
	const redirectUrl = resolveSessionFailureRedirectUrl(params);
	LoggerService.info(`🔄 [SessionRefresh] Redirecting to login (${logLabel})`, { redirectUrl, ...params });
	window.location.href = redirectUrl;
}

export function redirectToPublicFrontHomeFallback(): void {
	const base = resolveBrowserPublicFrontBaseUrl();
	window.location.href = base ? `${base}/` : "/";
}

type SessionFailureCleanupDeps = {
	stop: () => void;
	revokeLeCoffreToken: () => Promise<void>;
	disconnectSessionStore: (payload: AnyJwtPayload) => Promise<void>;
};

export async function runSessionFailureCleanup(
	payload: AnyJwtPayload,
	deps: SessionFailureCleanupDeps,
): Promise<void> {
	try {
		LoggerService.info("🔄 [SessionRefresh] Handling session failure, cleaning up and redirecting");
		deps.stop();
		const isCustomer = isCustomerJwtPayload(payload);
		const isThirdParty = isThirdPartyJwtPayload(payload);
		const isIdNotNotary = isUserJwtPayload(payload) && isIdNotNotaryPayload(payload);
		await deps.revokeLeCoffreToken();
		if (isIdNotNotary) {
			await revokeIdNotCookiesViaIframe();
		}
		if (isThirdParty) {
			sessionStorage.setItem("was_third_party", "true");
		}
		try {
			await deps.disconnectSessionStore(payload);
		} catch (error) {
			LoggerService.error("❌ [SessionRefresh] Erreur lors de la déconnexion des stores", { error });
		}
		clearBrowserSessionArtifacts();
		redirectAfterSessionCleared({ isThirdParty, isCustomer }, "session-failure");
	} catch (error) {
		LoggerService.error("❌ [SessionRefresh] Error during session failure handling", { error });
		redirectToPublicFrontHomeFallback();
	}
}

export async function runUnauthenticatedAccessCleanup(
	context: LeCoffreAuthContext,
	stop: () => void,
): Promise<void> {
	try {
		LoggerService.info("🔄 [SessionRefresh] Unauthenticated access on protected context — cleanup and redirect", {
			context,
		});
		stop();
		try {
			await disconnectStoreForAuthContext(context);
		} catch (error) {
			LoggerService.error("❌ [SessionRefresh] Error disconnecting store (unauthenticated)", { error, context });
		}
		clearBrowserSessionArtifacts();
		redirectAfterSessionCleared(
			{ isThirdParty: context === "thirdparty", isCustomer: context === "customer" },
			"unauthenticated",
		);
	} catch (error) {
		LoggerService.error("❌ [SessionRefresh] Error during unauthenticated redirect", { error });
		redirectToPublicFrontHomeFallback();
	}
}
